import React, { useMemo } from 'react';

import { useProductionForecastStore } from '../../hooks/useProductionForecastStore';

import type {
  BasicParams,
  ProductionRow,
} from './types';

import { ETH } from '../economics/ecoShared';

export default function DataPreparationPanel({
  projectId,
}: {
  projectId: number;
}) {
  const project = useProductionForecastStore(
    (state) => state.projects[String(projectId)],
  );

  const updateParamInStore = useProductionForecastStore(
    (state) => state.updateParam,
  );

  const addProductionRowInStore = useProductionForecastStore(
    (state) => state.addProductionRow,
  );

  const updateProductionRowInStore = useProductionForecastStore(
    (state) => state.updateProductionRow,
  );

  const deleteProductionRowInStore = useProductionForecastStore(
    (state) => state.deleteProductionRow,
  );

  const resetDataPreparation = useProductionForecastStore(
    (state) => state.resetDataPreparation,
  );

  const params = project?.params;
  const productionRows = project?.productionRows;

  const completion = useMemo(() => {
    if (!params) {
      return 0;
    }

    const values = Object.values(params);

    const filled = values.filter(
      (value) => String(value).trim() !== '',
    ).length;

    return Math.round(
      (filled / values.length) * 100,
    );
  }, [params]);

  if (!params || !productionRows) {
    return (
      <div
        style={{
          padding: 20,
          color: ETH.textMuted,
        }}
      >
        正在初始化产能预测数据...
      </div>
    );
  }

  const updateParam = (
    key: keyof BasicParams,
    value: string,
  ) => {
    updateParamInStore(
      projectId,
      key,
      value,
    );
  };

  const addProductionRow = () => {
    addProductionRowInStore(projectId);
  };

  const updateProductionRow = (
    id: number,
    key: Exclude<
      keyof ProductionRow,
      'id'
    >,
    value: string,
  ) => {
    updateProductionRowInStore(
      projectId,
      id,
      key,
      value,
    );
  };

  const deleteProductionRow = (
    id: number,
  ) => {
    deleteProductionRowInStore(
      projectId,
      id,
    );
  };

  const resetAll = () => {
    resetDataPreparation(projectId);
  };

  return (
    <div>
      {/* 顶部状态 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div>
          <h3 style={{ margin: 0 }}>
            数据准备
          </h3>

          <div
            style={{
              marginTop: 5,
              fontSize: 11,
              color: ETH.textMuted,
            }}
          >
            录入产能评价所需储层、井筒、压裂及生产动态数据
          </div>
        </div>

        <div
          style={{
            textAlign: 'right',
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: ETH.textMuted,
            }}
          >
            基础数据填写完整度
          </div>

          <div
            style={{
              marginTop: 3,
              fontSize: 20,
              fontWeight: 800,
              fontFamily: ETH.fontMono,
            }}
          >
            {completion}%
          </div>
        </div>
      </div>

      {/* 基础参数 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(3, minmax(0, 1fr))',
          gap: 14,
        }}
      >
        <ParameterCard title="储层参数">
          <Field
            label="原始地层压力"
            unit="MPa"
            value={params.reservoirPressure}
            onChange={(value) =>
              updateParam(
                'reservoirPressure',
                value,
              )
            }
          />

          <Field
            label="地层温度"
            unit="℃"
            value={params.reservoirTemperature}
            onChange={(value) =>
              updateParam(
                'reservoirTemperature',
                value,
              )
            }
          />

          <Field
            label="储层中深"
            unit="m"
            value={params.depth}
            onChange={(value) =>
              updateParam(
                'depth',
                value,
              )
            }
          />

          <Field
            label="有效厚度"
            unit="m"
            value={params.effectiveThickness}
            onChange={(value) =>
              updateParam(
                'effectiveThickness',
                value,
              )
            }
          />

          <Field
            label="孔隙度"
            unit="%"
            value={params.porosity}
            onChange={(value) =>
              updateParam(
                'porosity',
                value,
              )
            }
          />

          <Field
            label="渗透率"
            unit="mD"
            value={params.permeability}
            onChange={(value) =>
              updateParam(
                'permeability',
                value,
              )
            }
          />

          <Field
            label="含水饱和度"
            unit="%"
            value={params.waterSaturation}
            onChange={(value) =>
              updateParam(
                'waterSaturation',
                value,
              )
            }
          />
        </ParameterCard>

        <ParameterCard title="井筒参数">
          <SelectField
            label="井型"
            value={params.wellType}
            options={[
              '水平井',
              '直井',
            ]}
            onChange={(value) =>
              updateParam(
                'wellType',
                value,
              )
            }
          />

          <Field
            label="总井深"
            unit="m"
            value={params.totalDepth}
            onChange={(value) =>
              updateParam(
                'totalDepth',
                value,
              )
            }
          />

          <Field
            label="水平段长度"
            unit="m"
            value={params.horizontalLength}
            onChange={(value) =>
              updateParam(
                'horizontalLength',
                value,
              )
            }
          />

          <Field
            label="油管内径"
            unit="mm"
            value={params.tubingInnerDiameter}
            onChange={(value) =>
              updateParam(
                'tubingInnerDiameter',
                value,
              )
            }
          />
        </ParameterCard>

        <ParameterCard title="压裂参数">
          <Field
            label="压裂段数"
            value={params.stageCount}
            onChange={(value) =>
              updateParam(
                'stageCount',
                value,
              )
            }
          />

          <Field
            label="射孔簇数"
            value={params.clusterCount}
            onChange={(value) =>
              updateParam(
                'clusterCount',
                value,
              )
            }
          />

          <Field
            label="段间距"
            unit="m"
            value={params.stageSpacing}
            onChange={(value) =>
              updateParam(
                'stageSpacing',
                value,
              )
            }
          />

          <Field
            label="簇间距"
            unit="m"
            value={params.clusterSpacing}
            onChange={(value) =>
              updateParam(
                'clusterSpacing',
                value,
              )
            }
          />

          <Field
            label="压裂液量"
            unit="m³"
            value={params.fluidVolume}
            onChange={(value) =>
              updateParam(
                'fluidVolume',
                value,
              )
            }
          />

          <Field
            label="支撑剂量"
            unit="t"
            value={params.proppantMass}
            onChange={(value) =>
              updateParam(
                'proppantMass',
                value,
              )
            }
          />

          <Field
            label="平均排量"
            unit="m³/min"
            value={params.averageRate}
            onChange={(value) =>
              updateParam(
                'averageRate',
                value,
              )
            }
          />

          <Field
            label="裂缝半长"
            unit="m"
            value={params.fractureHalfLength}
            onChange={(value) =>
              updateParam(
                'fractureHalfLength',
                value,
              )
            }
          />

          <Field
            label="裂缝高度"
            unit="m"
            value={params.fractureHeight}
            onChange={(value) =>
              updateParam(
                'fractureHeight',
                value,
              )
            }
          />

          <Field
            label="裂缝导流能力"
            unit="D·cm"
            value={params.fractureConductivity}
            onChange={(value) =>
              updateParam(
                'fractureConductivity',
                value,
              )
            }
          />

          <Field
            label="SRV"
            unit="m³"
            value={params.srv}
            onChange={(value) =>
              updateParam(
                'srv',
                value,
              )
            }
          />
        </ParameterCard>
      </div>

      {/* 生产动态数据 */}
      <div
        style={{
          marginTop: 16,
          padding: 14,
          background: ETH.canvasBg,
          border: `1px solid ${ETH.headerBorder}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              生产动态数据
            </div>

            <div
              style={{
                marginTop: 4,
                fontSize: 11,
                color: ETH.textMuted,
              }}
            >
              录入日产气量、压力及累计产量等生产日报数据
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 8,
            }}
          >
            <button
              type="button"
              disabled
              title="Excel/CSV批量导入功能下一阶段接入"
              style={{
                ...secondaryButtonStyle,
                opacity: 0.55,
                cursor: 'not-allowed',
              }}
            >
              导入 CSV / Excel
            </button>

            <button
              type="button"
              onClick={addProductionRow}
              style={primaryButtonStyle}
            >
              + 新增记录
            </button>
          </div>
        </div>

        <div
          style={{
            overflowX: 'auto',
          }}
        >
          <table
            style={{
              width: '100%',
              minWidth: 1120,
              borderCollapse: 'collapse',
              fontSize: 11,
            }}
          >
            <thead>
              <tr>
                {[
                  '日期',
                  '生产时间 h',
                  '日产气量 10⁴m³/d',
                  '日产水量 m³/d',
                  '油压 MPa',
                  '套压 MPa',
                  '井底流压 MPa',
                  '累计产气 10⁴m³',
                  '操作',
                ].map((name) => (
                  <th
                    key={name}
                    style={thStyle}
                  >
                    {name}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {productionRows.map(
                (row) => (
                  <tr key={row.id}>
                    <td style={tdStyle}>
                      <input
                        type="date"
                        value={row.date}
                        onChange={(event) =>
                          updateProductionRow(
                            row.id,
                            'date',
                            event.target.value,
                          )
                        }
                        style={tableInputStyle}
                      />
                    </td>

                    <ProductionCell
                      value={row.productionHours}
                      onChange={(value) =>
                        updateProductionRow(
                          row.id,
                          'productionHours',
                          value,
                        )
                      }
                    />

                    <ProductionCell
                      value={row.gasRate}
                      onChange={(value) =>
                        updateProductionRow(
                          row.id,
                          'gasRate',
                          value,
                        )
                      }
                    />

                    <ProductionCell
                      value={row.waterRate}
                      onChange={(value) =>
                        updateProductionRow(
                          row.id,
                          'waterRate',
                          value,
                        )
                      }
                    />

                    <ProductionCell
                      value={row.tubingPressure}
                      onChange={(value) =>
                        updateProductionRow(
                          row.id,
                          'tubingPressure',
                          value,
                        )
                      }
                    />

                    <ProductionCell
                      value={row.casingPressure}
                      onChange={(value) =>
                        updateProductionRow(
                          row.id,
                          'casingPressure',
                          value,
                        )
                      }
                    />

                    <ProductionCell
                      value={row.bottomHolePressure}
                      onChange={(value) =>
                        updateProductionRow(
                          row.id,
                          'bottomHolePressure',
                          value,
                        )
                      }
                    />

                    <ProductionCell
                      value={row.cumulativeGas}
                      onChange={(value) =>
                        updateProductionRow(
                          row.id,
                          'cumulativeGas',
                          value,
                        )
                      }
                    />

                    <td
                      style={{
                        ...tdStyle,
                        textAlign: 'center',
                      }}
                    >
                      <button
                        type="button"
                        disabled={
                          productionRows.length <= 1
                        }
                        onClick={() =>
                          deleteProductionRow(
                            row.id,
                          )
                        }
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: ETH.textMuted,
                          cursor:
                            productionRows.length <= 1
                              ? 'not-allowed'
                              : 'pointer',
                        }}
                      >
                        删除
                      </button>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 数据质控 */}
      <div
        style={{
          marginTop: 16,
          display: 'grid',
          gridTemplateColumns:
            'repeat(3, minmax(0, 1fr))',
          gap: 12,
        }}
      >
        <QualityCard
          title="完整性检查"
          value={`${completion}%`}
          description="检查基础参数是否存在缺项"
        />

        <QualityCard
          title="生产记录"
          value={`${productionRows.length} 条`}
          description="已录入生产动态记录数量"
        />

        <QualityCard
          title="数据质控"
          value="待执行"
          description="异常值、关井段和缺失值检查"
        />
      </div>

      {/* 底部按钮 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          marginTop: 16,
        }}
      >
        <button
          type="button"
          onClick={resetAll}
          style={secondaryButtonStyle}
        >
          重置
        </button>

        <button
          type="button"
          disabled
          title="后端数据持久化接口尚未接入"
          style={{
            ...primaryButtonStyle,
            opacity: 0.55,
            cursor: 'not-allowed',
          }}
        >
          保存数据
        </button>
      </div>
    </div>
  );
}

function ParameterCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        padding: 14,
        background: ETH.canvasBg,
        border: `1px solid ${ETH.headerBorder}`,
      }}
    >
      <div
        style={{
          marginBottom: 12,
          fontSize: 14,
          fontWeight: 700,
        }}
      >
        {title}
      </div>

      <div
        style={{
          display: 'grid',
          gap: 9,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  unit,
  value,
  onChange,
}: {
  label: string;
  unit?: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label
      style={{
        display: 'grid',
        gridTemplateColumns:
          '105px 1fr 55px',
        gap: 8,
        alignItems: 'center',
        fontSize: 11,
      }}
    >
      <span>{label}</span>

      <input
        type="number"
        value={value}
        placeholder="请输入"
        onChange={(event) =>
          onChange(event.target.value)
        }
        style={inputStyle}
      />

      <span
        style={{
          color: ETH.textMuted,
        }}
      >
        {unit ?? ''}
      </span>
    </label>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  return (
    <label
      style={{
        display: 'grid',
        gridTemplateColumns:
          '105px 1fr 55px',
        gap: 8,
        alignItems: 'center',
        fontSize: 11,
      }}
    >
      <span>{label}</span>

      <select
        value={value}
        onChange={(event) =>
          onChange(event.target.value)
        }
        style={inputStyle}
      >
        {options.map((item) => (
          <option
            key={item}
            value={item}
          >
            {item}
          </option>
        ))}
      </select>

      <span />
    </label>
  );
}

function ProductionCell({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <td style={tdStyle}>
      <input
        type="number"
        value={value}
        onChange={(event) =>
          onChange(event.target.value)
        }
        style={tableInputStyle}
      />
    </td>
  );
}

function QualityCard({
  title,
  value,
  description,
}: {
  title: string;
  value: string;
  description: string;
}) {
  return (
    <div
      style={{
        padding: 14,
        background: ETH.canvasBg,
        border: `1px solid ${ETH.headerBorder}`,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: ETH.textMuted,
        }}
      >
        {title}
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize: 20,
          fontWeight: 800,
          fontFamily: ETH.fontMono,
        }}
      >
        {value}
      </div>

      <div
        style={{
          marginTop: 4,
          fontSize: 10,
          color: ETH.textMuted,
        }}
      >
        {description}
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '6px 8px',
  border: `1px solid ${ETH.headerBorder}`,
  background: ETH.bg,
  color: ETH.text,
  outline: 'none',
};

const tableInputStyle: React.CSSProperties = {
  width: '100%',
  minWidth: 90,
  boxSizing: 'border-box',
  padding: '5px 6px',
  border: `1px solid ${ETH.headerBorder}`,
  background: ETH.bg,
  color: ETH.text,
  outline: 'none',
};

const thStyle: React.CSSProperties = {
  padding: '7px 8px',
  borderBottom: `1px solid ${ETH.headerBorder}`,
  textAlign: 'center',
  fontWeight: 700,
  color: ETH.textSec,
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '5px 6px',
  borderBottom: `1px solid ${ETH.headerBorder}`,
};

const primaryButtonStyle: React.CSSProperties = {
  padding: '7px 12px',
  border: `1px solid ${ETH.headerBorder}`,
  background: ETH.bg,
  color: ETH.text,
  cursor: 'pointer',
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: '7px 12px',
  border: `1px solid ${ETH.headerBorder}`,
  background: ETH.canvasBg,
  color: ETH.textSec,
  cursor: 'pointer',
};